// src/components/Navbar.jsx
import React, { useEffect, useState } from "react";
import { motion, LayoutGroup } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";

const NAV_ITEMS = [
  { id: "about", label: "About", sectionId: "home" },
  { id: "skills", label: "Skills", sectionId: "expertise" },
  { id: "projects", label: "Projects", sectionId: "case-studies" },
  { id: "public", label: "Public", sectionId: "public" },
];


const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [active, setActive] = useState("about");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [brandHover, setBrandHover] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 40);
    };
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (location.pathname !== "/") return;
  
    const sections = NAV_ITEMS.map((x) => ({
      navId: x.id,
      el: document.getElementById(x.sectionId),
    })).filter((x) => x.el);
  
    if (!sections.length) return;
  
    let rafId = null;
  
    const observer = new IntersectionObserver(
      (entries) => {
        // pick the most visible intersecting section
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => (b.intersectionRatio || 0) - (a.intersectionRatio || 0));
  
        if (!visible.length) return;
  
        const top = visible[0].target;
        const match = sections.find((s) => s.el === top);
        if (!match) return;
  
        // avoid thrashing state during scroll
        if (rafId) cancelAnimationFrame(rafId);
        rafId = requestAnimationFrame(() => setActive(match.navId));
      },
      {
        // tune this for when you want the highlight to switch
        root: null,
        threshold: [0.25, 0.4, 0.55, 0.7],
      }
    );
  
    sections.forEach((s) => observer.observe(s.el));
  
    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      observer.disconnect();
    };
  }, [location.pathname]);
  

  // const idMap = {
  //   home: "home",          // brand click → Hero
  //   about: "home",         // About → Hero
  //   skills: "expertise",   // Skills → Expertise
  //   projects: "case-studies",
  //   contact: "contact",
  // };

  const scrollToId = (targetId) => {
    const el = document.getElementById(targetId);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const handleNavClick = (navId) => {
    setActive(navId);
    setMobileOpen(false);
  
    // brand/home click -> hero
    if (navId === "home") {
      const targetId = "home";
      if (location.pathname !== "/") {
        navigate("/", { state: { scrollTo: targetId } });
        return;
      }
      scrollToId(targetId);
      return;
    }
  
    const item = NAV_ITEMS.find((x) => x.id === navId);
    const targetId = item?.sectionId || "home";
  
    if (location.pathname !== "/") {
      navigate("/", { state: { scrollTo: targetId } });
      return;
    }
  
    scrollToId(targetId);
  };
  

  const handleContactClick = () => {
    setActive("contact");
    setMobileOpen(false);

    const targetId = "contact";

    if (location.pathname !== "/") {
      navigate("/", { state: { scrollTo: targetId } });
      return;
    }

    scrollToId(targetId);
  };

  return (
    <motion.header
      className={`navbar ${scrolled ? "navbar--scrolled" : ""}`}
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.4 }}
    >
      <div className="navbar-inner">
        {/* Brand */}
        <motion.button
          className="nav-brand"
          onClick={() => handleNavClick("home")}
          onHoverStart={() => setBrandHover(true)}
          onHoverEnd={() => setBrandHover(false)}
          whileTap={{ scale: 0.97 }}
        >
          <motion.span
            key={brandHover ? "alt" : "default"}
            className={`nav-brand-label ${
              brandHover ? "nav-brand-label--alt" : ""
            }`}
            animate={{ opacity: [0, 1], y: [4, 0] }}
            transition={{ duration: 0.18 }}
          >
            {brandHover ? "<JohnMendez/>" : "JOHN MENDEZ"}
          </motion.span>
        </motion.button>

        {/* Desktop nav */}
        <LayoutGroup>
          <nav className="nav-links">
            {NAV_ITEMS.map((item) => (
              <button
                key={item.id}
                className={`nav-link ${
                  active === item.id ? "nav-link--active" : ""
                }`}
                onClick={() => handleNavClick(item.id)}
              >
                <span className="nav-link-label">{item.label}</span>
                {active === item.id && (
                  <motion.span
                    layoutId="nav-underline"
                    className="nav-link-underline"
                    transition={{ type: "spring", stiffness: 400, damping: 30 }}
                  />
                )}
              </button>
            ))}

            <motion.button
              className={`nav-cta ${
                active === "contact" ? "nav-cta--active" : ""
              }`}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              onClick={handleContactClick}
            >
              Contact
            </motion.button>
          </nav>
        </LayoutGroup>

        {/* Mobile hamburger */}
        <button
          className={`nav-toggle ${mobileOpen ? "nav-toggle--open" : ""}`}
          onClick={() => setMobileOpen((open) => !open)}
          aria-label="Toggle navigation"
        >
          <span />
          <span />
        </button>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <motion.nav
          className="nav-menu-mobile"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
        >
          {NAV_ITEMS.map((item) => (
            <button
              key={item.id}
              className={`nav-menu-mobile-item ${
                active === item.id ? "nav-menu-mobile-item--active" : ""
              }`}
              onClick={() => handleNavClick(item.id)}
            >
              {item.label}
            </button>
          ))}
          <button
            className={`nav-menu-mobile-item nav-menu-mobile-item--cta ${
              active === "contact" ? "nav-menu-mobile-item--active" : ""
            }`}
            onClick={handleContactClick}
          >
            Contact
          </button>
        </motion.nav>
      )}
    </motion.header>
  );
};

export default Navbar;
